import React, { createContext, useContext, useState, useCallback } from 'react';
import ConfirmDialog from './ConfirmDialog';

const ConfirmContext = createContext(null);

/**
 * Hook to access the confirm dialog functionality
 * Returns a function that shows the dialog and returns a promise
 * 
 * Usage:
 * const confirm = useConfirm();
 * const result = await confirm({
 *   title: 'Delete Item',
 *   message: 'Are you sure?',
 *   type: 'danger'
 * });
 * if (result) { // User confirmed }
 */
export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

/**
 * Confirm Provider Component
 * Provides a programmatic way to show confirmation dialogs
 */
export const ConfirmProvider = ({ children }) => {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'confirm',
    onConfirm: null,
    onCancel: null,
  });

  const [loading, setLoading] = useState(false);

  /**
   * Show a confirmation dialog
   * @param {Object} options - Dialog options
   * @returns {Promise<boolean>} - Resolves to true if confirmed, false if cancelled
   */
  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        title: options.title || 'Confirm Action',
        message: options.message || 'Are you sure you want to proceed?',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        type: options.type || 'confirm',
        onConfirm: async () => {
          if (options.onConfirm) {
            setLoading(true);
            try {
              await options.onConfirm();
            } finally {
              setLoading(false);
            }
          }
          setDialogState((prev) => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setDialogState((prev) => ({ ...prev, isOpen: false }));
          resolve(false);
        },
      });
    });
  }, []);

  /**
   * Convenience method for danger confirmations
   */
  confirm.danger = useCallback(
    (options = {}) => confirm({ ...options, type: 'danger' }),
    [confirm]
  );

  /**
   * Convenience method for warning confirmations
   */
  confirm.warning = useCallback(
    (options = {}) => confirm({ ...options, type: 'warning' }),
    [confirm]
  );

  const handleClose = useCallback(() => {
    if (!loading && dialogState.onCancel) {
      dialogState.onCancel();
    }
  }, [dialogState, loading]);

  const handleConfirm = useCallback(() => {
    if (dialogState.onConfirm) {
      dialogState.onConfirm();
    }
  }, [dialogState]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title={dialogState.title}
        message={dialogState.message}
        confirmText={dialogState.confirmText}
        cancelText={dialogState.cancelText}
        type={dialogState.type}
        loading={loading}
      />
    </ConfirmContext.Provider>
  );
};

export default ConfirmContext;
